import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { faLoader, faRocket } from "@fortawesome/pro-regular-svg-icons";

import { useNavigate } from "react-router-dom";

import { FileUploader } from "react-drag-drop-files";

import { DeleteOutlineRounded } from "@mui/icons-material";

import { CircularProgress } from "@mui/material";

import UploadCloud from "@assets/icons/upload-cloud.svg?react";

import { Icon, Modal } from "@/components";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { PermissionRoles } from "@/enum";
import { useGetCourseVersionsQuery } from "@/pages/Private/redux/courses/courses.api";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { ButtonColor } from "@/components/Button/types";
import { FlagHeader } from "@/components/Modals/FlagHeader";
import { useAppSelector } from "@/redux/hooks";

import {
	useStartScenarioMutation,
	useSendUploadedFilesMutation,
	useRemoveUploadedFileMutation,
	useRemoveAllUploadedFilesMutation,
} from "@/pages/Private/redux/course-progress/course-progress.api";

import { ROUTE_CONFIG } from "@/routes/config";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { useGetDefaultParticipantsQuery } from "@/pages/Private/redux/default-participants/default-participants.api";

import { Courses } from "../schema/courses";

interface IStartCourseModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	trainingInfo?: Courses;
}

export interface TempUploadedFile {
	id: number;
	name: string;
	pageCount: number;
	companyId: number;
	userId: number;
}

export default function StartCourseModal({
	isOpen,
	setIsOpen,
	trainingInfo,
}: IStartCourseModalProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const ts = (key: string, options = {}) => t(`courses.${key}`, options);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [startCourse, { isLoading, error: requestError }] = useStartScenarioMutation();
	const [sendUploadedFiles] = useSendUploadedFilesMutation();
	const [selectedCourseId, setSelectedCourseId] = useState(trainingInfo?.id ?? -1);
	/**
	 * Which version to run — BCM360-139. Null is the published one, which is what everyone
	 * else always gets: only a global admin may reach past it, and only from here, so the
	 * normal start and every VR start are untouched.
	 */
	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
	const { hasPermissions } = useHasPermissions();
	const isGlobalAdmin = hasPermissions([PermissionRoles.GLOBAL_ADMIN]);
	const { data: courseVersions } = useGetCourseVersionsQuery(selectedCourseId, {
		skip: !isGlobalAdmin || selectedCourseId === -1,
	});

	// Reset when the scenario changes: a version id belongs to one scenario, and carrying
	// it across would ask the server to start another scenario's history.
	useEffect(() => {
		setSelectedVersionId(null);
	}, [selectedCourseId]);

	// A handful of entries built from an already-cached list — memoising it would only pin
	// `ts` into a dependency array for nothing.
	const versionOptions = [
		{ id: 0, title: ts("start.publishedVersion") },
		...(courseVersions ?? []).map((version) => ({
			id: version.id,
			title: version.isDraft
				? ts("start.draftVersion")
				: version.label || ts("start.versionFallback", { id: version.id }),
		})),
	];
	const [error, setError] = useState("");
	const currentUserAuth = useAppSelector((state) => state.auth?.user);

	const maxFileSizeInBytes = 50 * 1024 * 1024;
	const [isFileLoading, setIsFileLoading] = useState(false);
	const [removeUploadedFile] = useRemoveUploadedFileMutation();
	const [removeAllUploadedFiles] = useRemoveAllUploadedFilesMutation();
	const [uploadedFiles, setUploadedFiles] = useState<TempUploadedFile[]>([]);

	const [defaultParticipantIds, setDefaultParticipantIds] = useState<number[]>([]);
	const {
		data: defaultParticipantsData,
		isLoading: isDefaultParticipantsLoading,
		isError: isDefaultParticipantsError,
	} = useGetDefaultParticipantsQuery();

	async function handleRemoveUploadedFile(fileId: number) {
		try {
			await removeUploadedFile(fileId).unwrap();

			setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
		} catch (err) {
			console.error(err);
		}
	}

	async function handleCloseStartModal(uploadedFiles: TempUploadedFile[]) {
		try {
			const removedFileIds = uploadedFiles.map((file) => file.id);

			setIsOpen(false);
			if (removedFileIds.length > 0) {
				await removeAllUploadedFiles(removedFileIds).unwrap();
				setUploadedFiles([]);
			}
		} catch (err) {
			console.error(err);
		}
	}

	async function handleFileUpload(file: File) {
		setIsFileLoading(true);
		const selectedFiles = file instanceof FileList ? Array.from(file) : file;

		if (Array.isArray(selectedFiles)) {
			for (const file of selectedFiles) {
				const formData = new FormData();

				formData.append("file", file);
				try {
					const res = await sendUploadedFiles(formData).unwrap();

					const newUploadedFile: TempUploadedFile = {
						...res,
						id: res.uploadedFileId,
					};

					setUploadedFiles((prev) => [...prev, newUploadedFile]);
				} catch (error) {
					console.error("Upload error", error);
				}
			}

			setIsFileLoading(false);
		} else {
			const formData = new FormData();

			formData.append("file", selectedFiles);
			try {
				const res = await sendUploadedFiles(formData).unwrap();

				const newUploadedFile: TempUploadedFile = {
					...res,
					id: res.uploadedFileId,
				};

				setUploadedFiles((prev) => [...prev, newUploadedFile]);
			} catch (error) {
				console.error("Upload error", error);
			} finally {
				setIsFileLoading(false);
			}
		}
	}

	const onStartCourseHandler = async () => {
		if (selectedCourseId !== -1 && currentUserAuth) {
			setError("");
			try {
				const result = await startCourse({
					courseId: selectedCourseId,
					userId: currentUserAuth.id,
					versionId: selectedVersionId ?? undefined,
					uploadedTempFiles: uploadedFiles.map((file) => file.id),
					defaultParticipantIds,
				}).unwrap();

				if (result?.id) {
					navigate(`${ROUTE_CONFIG.COURSES}/${result.id}/progress`);
				}
			} catch (err) {
				console.error(err);
			}

			setIsOpen(false);
		} else {
			setError(ts("start.please-select"));
		}
	};

	return (
		<Modal
			aboveHeader={<FlagHeader />}
			dialogContentClassName="!p-2"
			disableClose={isFileLoading}
			disableSave={isFileLoading}
			handleClose={() => handleCloseStartModal(uploadedFiles)}
			handleSave={onStartCourseHandler}
			isLoading={isLoading}
			isOpened={isOpen}
			size="md"
			submitButtonColor={ButtonColor.ACTION}
			submitButtonIcon={
				<div className="mr-2 flex items-center justify-center">
					<Icon className="w-4 h-4" icon={faRocket} />
				</div>
			}
			submitButtonText={ts("start.title")}
			subtitle={ts("start.start-training-description", { trainingName: trainingInfo?.name })}
			title={ts("start.title")}
		>
			{/*
			 * Only a global admin sees this, and the server enforces the same — it is how
			 * unpublished work gets run at all, since nothing else offers a scenario that is
			 * not live.
			 */}
			{isGlobalAdmin && versionOptions.length > 1 && (
				<div className="mt-2">
					<Dropdown
						data={versionOptions}
						handleSelect={(item) => setSelectedVersionId(Number(item.id) || null)}
						label={ts("start.versionLabel")}
						value={
							versionOptions.find((option) => option.id === (selectedVersionId ?? 0)) ??
							versionOptions[0]
						}
					/>
					<p className="mt-1 text-xs text-subtle">{ts("start.versionHint")}</p>
				</div>
			)}

			<div className="flex gap-0 min-h-[330px] max-h-[350px] mt-2 rounded-xl overflow-hidden border border-gray-100">
				{/* Left — Participants */}
				<div className="w-1/2 bg-white p-4 flex flex-col gap-3">
					<p className="text-xs font-semibold tracking-wide text-primary-blue-dark">
						{t("defaultParticipants.selectParticipants")}
					</p>

					{isDefaultParticipantsLoading ? (
						<div className="flex flex-1 items-center justify-center">
							<CircularProgress size={20} />
						</div>
					) : isDefaultParticipantsError ? (
						// A failed request is not an empty roster — say so, or the participants
						// look deleted (BCM360-341).
						<p className="text-sm text-red-500">{t("defaultParticipants.loadError")}</p>
					) : !defaultParticipantsData?.data?.length ? (
						<p className="text-sm text-primary-blue-dark">
							{t("defaultParticipants.noParticipants")}
						</p>
					) : (
						<div className="flex flex-col gap-1  overflow-y-auto  max-h-60 bb-scrollbar-darker ">
							{defaultParticipantsData.data.map((participant) => {
								const isSelected = defaultParticipantIds.includes(participant.id);

								return (
									<div
										key={participant.id}
										className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all border ${
											isSelected
												? "bg-white border-accent-strong shadow-sm"
												: "bg-white border-transparent hover:border-gray-200"
										}`}
										onClick={() =>
											setDefaultParticipantIds((prev) =>
												prev.includes(participant.id)
													? prev.filter((i) => i !== participant.id)
													: [...prev, participant.id]
											)
										}
									>
										<div
											className={`w-4 h-4 rounded flex items-center justify-center border-2 flex-shrink-0 transition-all ${
												isSelected
													? "bg-accent-strong border-accent-strong"
													: "border-gray-300 bg-white"
											}`}
										>
											{isSelected && (
												<svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 8">
													<path
														d="M1 4l3 3 5-6"
														stroke="currentColor"
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
													/>
												</svg>
											)}
										</div>
										<div className="min-w-0 w-full flex justify-between">
											<div className="">
												<p className=" capitalize text-sm font-medium text-primary-blue-dark truncate">
													{participant.firstName} {participant.lastName}
												</p>
												<p className="capitalize text-xs font-normal text-primary-blue-dark ">
													{participant.internalRoles}
												</p>
											</div>
											<p className="capitalize text-xs font-normal text-primary-blue-dark ">
												{t(`courses.progress.permissions.${participant.scenarioRoles}`)}
											</p>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Divider */}
				<div className="w-px bg-gray-100" />

				{/* Right — File Upload */}
				<div className="w-1/2 p-4 flex flex-col gap-3">
					<p className="text-xs font-semibold tracking-wide text-primary-blue-dark">
						{ts("uploadedFiles.selectFile")}
					</p>

					{uploadedFiles.length > 0 && (
						<div className="flex flex-col gap-1  overflow-y-auto  max-h-60 bb-scrollbar-darker ">
							{uploadedFiles.map((file) => (
								<div
									key={file.id}
									className="flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 "
								>
									<div className="min-w-0">
										<p className="text-sm font-medium text-primary-blue-dark truncate max-w-[180px]">
											{file.name}
										</p>
										<p className="text-xs font-normal text-primary-blue-dark ">
											{file.pageCount} {ts("progress.pages")}
										</p>
									</div>
									<button
										className=" text-red-500 transition-colors ml-2"
										onClick={(e) => {
											e.stopPropagation();
											handleRemoveUploadedFile(file.id);
										}}
									>
										<DeleteOutlineRounded fontSize="small" />
									</button>
								</div>
							))}
						</div>
					)}

					<FileUploader
						disabled={isFileLoading}
						handleChange={(file: File | File[]) => {
							const singleFile = Array.isArray(file) ? file[0] : file;

							handleFileUpload(singleFile);
						}}
						maxSize={Math.round(maxFileSizeInBytes / 1024 / 1024)}
						multiple={true}
						name="file"
						types={["PDF", "PPTX", "DOCX"]}
					>
						<div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl p-6 cursor-pointer hover:border-accent-strong transition-colors text-center">
							{isFileLoading ? (
								<Icon className="animate-spin" icon={faLoader} size="lg" />
							) : (
								<>
									<SvgIcon className="h-8 w-8 text-gray-500 mb-2" svgIcon={UploadCloud} />
									<p className="text-sm text-gray-700">{t("courses.upload.title")}</p>
									<p className="text-xs text-gray-700 mt-1">
										{t("courses.upload.description", {
											sizeInMB: Math.round(maxFileSizeInBytes / 1024 / 1024),
										})}
									</p>
								</>
							)}
						</div>
					</FileUploader>
				</div>
			</div>
		</Modal>
	);
}
